import React, { memo, type RefObject } from 'react';
import { RowConfig, ColumnConfig, EmptyState } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { TableRow } from '../molecules';
import { SkeletonRow } from './SkeletonRow';
import { EmptyTableState } from './EmptyTableState';

interface TableBodyProps<T = unknown> {
  rows: RowConfig<T>[];
  columns: ColumnConfig<T>[];
  /** Anchos de `<th>` medidos en `TableContent` (ResizeObserver); no es estado reactivo. */
  columnWidthsRef?: RefObject<Map<string, number>>;
  loading?: boolean;
  empty?: EmptyState;
  selectedIds?: (string | number)[];
  expandable?: boolean;
  showSelection?: boolean;
  showExpand?: boolean;
  stickyCheckbox?: boolean;
  stickyActions?: boolean;
  onRowClick?: (row: RowConfig<T>) => void;
  onRowExpand?: (row: RowConfig<T>) => void;
  onRowSelect?: (row: RowConfig<T>) => void;
  onCellFocus?: (rowId: string | number, columnId: string) => void;
  isCellFocused?: (rowId: string | number, columnId: string) => boolean;
  onCellChange?: (rowId: string | number, columnId: string, value: unknown) => void;
  hasColumnMaxWidthAuto?: boolean;
  isRowSelectionDisabled?: (row: RowConfig<T>) => boolean;
}

const TableBodyInner = <T, >({
  rows,
  columns,
  columnWidthsRef,
  loading = false,
  empty,
  selectedIds = [],
  expandable = false,
  showSelection = false,
  showExpand = false,
  stickyCheckbox = true,
  stickyActions = true,
  onRowClick,
  onRowExpand,
  onRowSelect,
  onCellFocus,
  isCellFocused,
  onCellChange,
  hasColumnMaxWidthAuto = false,
  isRowSelectionDisabled,
}: TableBodyProps<T>) => {
  const visibleColumns = columns.filter((col) => col.visible !== false);
  const colSpan = visibleColumns.length + (showSelection ? 1 : 0) + (showExpand ? 1 : 0);

  /** Solo borde inferior en la última fila del cuerpo: separación cabecera↔filas (thead) + cierre del bloque. */
  const bodyRowBorderClass = '[&>tr:not(:last-child)>td]:ui-border-b-0';

  if (loading) {
    return (
      <tbody className={cn(bodyRowBorderClass)}>
        {Array.from({ length: 10 }).map((_, index) => (
          <SkeletonRow
            key={index}
            columns={visibleColumns as ColumnConfig<unknown>[]}
            showSelection={showSelection}
            showExpand={showExpand}
          />
        ))}
      </tbody>
    );
  }

  if (rows.length === 0 && empty) {
    return (
      <tbody className={cn(bodyRowBorderClass)}>
        <tr>
          <td colSpan={colSpan} className="ui-p-0">
            <EmptyTableState {...empty} />
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody className={cn(bodyRowBorderClass)}>
      {rows.map((row) => (
        <TableRow
          key={row.id}
          row={row}
          columns={columns}
          columnWidthsRef={columnWidthsRef}
          hasColumnMaxWidthAuto={hasColumnMaxWidthAuto}
          selected={selectedIds.includes(row.id)}
          selectedIds={selectedIds}
          expandable={expandable}
          showSelection={showSelection}
          showExpand={showExpand}
          stickyCheckbox={stickyCheckbox}
          stickyActions={stickyActions}
          onSelect={onRowSelect}
          onExpand={onRowExpand}
          onClick={onRowClick}
          onCellFocus={onCellFocus}
          isCellFocused={isCellFocused}
          onCellChange={onCellChange}
          isRowSelectionDisabled={isRowSelectionDisabled}
        />
      ))}
    </tbody>
  );
};

export const TableBody = memo(TableBodyInner) as <T>(
  props: TableBodyProps<T>
) => React.ReactElement | null;
